import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { SERVICES } from "@/data/services";

export const Services = ({ showHeader = true }) => {
  const [open, setOpen] = useState(0);

  return (
    <section id="services" data-testid="services-section" className="relative py-28 lg:py-36">
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-[#6D00FF]/8 blur-[160px] rounded-full pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {showHeader && (
          <div className="max-w-2xl mb-16 lg:mb-20">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
                What We Do
              </p>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[1.05]">
                Everything your brand needs to move from{" "}
                <span className="text-gradient-purple">idea to impact.</span>
              </h2>
            </Reveal>
          </div>
        )}

        <div>
          {SERVICES.map((s, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={s.n} delay={i * 0.05} y={24}>
                <div
                  data-testid={`service-card-${i}`}
                  onMouseEnter={() => setOpen(i)}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className={`group cursor-pointer border-t border-white/[0.07] last:border-b transition-colors duration-500 ${
                    isOpen ? "bg-white/[0.02]" : ""
                  }`}
                >
                  <div className="flex items-center gap-5 sm:gap-10 py-7 sm:py-9 px-4 sm:px-6">
                    <span
                      className={`font-display text-xs sm:text-sm font-bold tracking-[0.2em] transition-colors duration-500 ${
                        isOpen ? "text-[#C43CFF]" : "text-[#8B2CFF]/60"
                      }`}
                    >
                      {s.n} /
                    </span>
                    <h3
                      className={`font-display text-2xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight transition-colors duration-500 ${
                        isOpen ? "text-white" : "text-white/30"
                      }`}
                    >
                      {s.title}
                    </h3>
                    <span className="hidden md:block ml-auto text-xs uppercase tracking-[0.18em] text-[#A6A6B0]/70">
                      {s.tags}
                    </span>
                    <ArrowUpRight
                      className={`w-5 h-5 sm:w-6 sm:h-6 shrink-0 ml-auto md:ml-0 transition-[color,transform] duration-500 ${
                        isOpen ? "text-[#C43CFF] rotate-90" : "text-white/25"
                      }`}
                    />
                  </div>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 sm:px-6 pb-9 grid md:grid-cols-2 gap-6 md:gap-12">
                          <p className="text-sm sm:text-base text-[#A6A6B0] leading-relaxed max-w-lg">
                            {s.desc}
                          </p>
                          <div>
                            <div className="flex flex-wrap gap-2.5">
                              {s.deliverables.map((d) => (
                                <span
                                  key={d}
                                  className="text-[11px] uppercase tracking-[0.14em] text-[#E8B6FF]/90 border border-[#8B2CFF]/30 rounded px-3 py-1.5"
                                >
                                  {d}
                                </span>
                              ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-7 gap-y-4 mt-6">
                              <Link
                                to={`/services/${s.slug}`}
                                data-testid={`service-link-${s.slug}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#C43CFF] hover:text-[#E8B6FF] transition-colors duration-300"
                              >
                                Explore This Service <ArrowRight className="w-3.5 h-3.5" />
                              </Link>
                              <Link
                                to="/contact"
                                data-testid={`service-cta-${i}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#A6A6B0] hover:text-white transition-colors duration-300"
                              >
                                Start With This <ArrowUpRight className="w-3.5 h-3.5" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
